import {createSearchParams, useNavigate} from 'react-router-dom'
import {useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Card, Dropdown, Flex, Form, Layout, Modal, Space, Spin, Typography,} from 'antd'
import {
    CheckCircleFilled,
    CloseCircleFilled,
    DeleteOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    MoreOutlined,
    SyncOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons'
import {PromoteChangesContext, SET_PROMOTE_CHANGES_DATA, SHOW_SELECT_FILES_POPUP,} from './context/context'
import {NavContext} from '../../context/global_context'
import { notifyError } from '../../utils/notifications'
import {deleteRequest, getRequest, postRequest} from '../../services/base'
import InputText from '../common/InputText'
import Button from '../common/Button'
import PromoteChangesTable from './PromoteChangesTable'
import SelectVehicles from './select_vehicles'
import SelectFilesModal from './select_files'
import PromotedFilesModal from './promoted_files'
import DeleteFilesModal from './delete_files'
import useDebouncedValue from '../../utils/common_function'
import {getSessionStorageData} from '../../utils/SessionStorage'
import {downloadErrorLogs, downloadFileBlob} from '../../utils/common'
import {toString} from '../../utils/json'
import {
    DELETE_PROMOTED_FILES,
    PROMOTE_CHANGES,
    PUSH_TO_GIT,
    TRACER_PREVIEW,
    TRACER_PREVIEW_MGMT,
} from '../../constants/permission'

const { Header, Content } = Layout

const PromoteChangesList = () => {
  const navigate = useNavigate()
  const intl = useIntl()

  const [state, dispatch] = useContext(PromoteChangesContext)
  const [navState, navDispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const [branchConfirmed, setBranchConfirmed] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  const [filteredVehicles, setFilteredVehicles] = useState([])
  const [activeStatus, setActiveStatus] = useState('all')
  const debouncedSearch = useDebouncedValue(searchValue, 500)
  const pollTimeoutRef = useRef(null)

  const [previewVehicle, setPreviewVehicle] = useState(null)
  const [previewDetail, setPreviewDetail] = useState({})
  const [previewLoading, setPreviewLoading] = useState(false)

  const [deleteVehicle, setDeleteVehicle] = useState(null)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const getActiveBranchDetails = useCallback(
    async (showLoader = false) => {
      if (showLoader) {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({
              id: 'promoteChanges.loadingBranchData',
            }),
            showLoader: true,
          },
          notification: {},
        })
      }

      let res = await getRequest(`/api/promote_changes/active_branch/`)
      if (res?.status === false) {
        navDispatch({ type: 'loader', data: {}, notification: {} })
        notifyError(navDispatch, intl.formatMessage({ id: 'promoteChanges.branchLoadError' }))
        return
      }
      dispatch({ type: SET_PROMOTE_CHANGES_DATA, data: res.data })
      navDispatch({
        type: 'loader',
        data: {},
        notification: {},
      })
      if (res?.data?.is_processing) {
        clearTimeout(pollTimeoutRef.current)
        pollTimeoutRef.current = setTimeout(() => {
          getActiveBranchDetails(false)
        }, 60000)
      }
    },
    [navDispatch, dispatch, intl]
  )

  const validate = useCallback(async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'promoteChanges.loadingData' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(`/api/pre/promote_changes/validate/`)
    if (res?.status === false) {
      navDispatch({ type: 'loader', data: {}, notification: {} })
      notifyError(navDispatch, intl.formatMessage({ id: 'promoteChanges.branchLoadError' }))
      return
    }
    setBranchConfirmed(res?.data ? true : false)
    navDispatch({
      type: 'loader',
      data: {},
      notification: {},
    })
  }, [navDispatch, intl])

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'promoteChanges.header' }),
        sub_header: intl.formatMessage({ id: 'promoteChanges.subHeader' }),
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: vehicle_redirect_url,
        full_width_container: true,
      },
    })

    validate()

    return () => {
      clearTimeout(pollTimeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (branchConfirmed === true) {
      getActiveBranchDetails(true)
    }
  }, [branchConfirmed, getActiveBranchDetails])

  useEffect(() => {
    const vehicles = state?.data?.vehicles || []
    if (debouncedSearch) {
      setFilteredVehicles(
        vehicles.filter((item) =>
          item.name.toLowerCase().includes(debouncedSearch.toLowerCase())
        )
      )
    } else {
      setFilteredVehicles([...vehicles])
    }
  }, [debouncedSearch, state?.data?.vehicles])

  const confirmDeleteBranch = () => {
    modal.confirm({
      title: intl.formatMessage({ id: 'promoteChanges.closeBranchTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'promoteChanges.deleteBranchConfirm' },
        { branchName: state?.data?.branch?.name }
      ),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'promoteChanges.delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'promoteChanges.cancel' }),
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({
              id: 'promoteChanges.deletingBranch',
            }),
            showLoader: true,
          },
          notification: {},
        })

        let res = await deleteRequest(`/api/promote_changes/delete/branch/`)
        // Without this the else below reported "Branch Deleted successfully"
        // and reloaded the page, for a branch that was never deleted.
        if (res?.status === false) {
          notifyError(navDispatch, intl.formatMessage({ id: 'promoteChanges.deleteBranchError' }))
          return
        }
        if (res?.data) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({
                id: 'promoteChanges.branchDeletedFromGit',
              }),
            },
          })
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'info',
              msg: intl.formatMessage({ id: 'promoteChanges.branchDeleted' }),
            },
          })
        }
        setTimeout(() => {
          window.location.reload()
        }, 500)
      },
    })
  }

  const downloadUpdateJson = () => {
    if (state?.data?.vehicles?.length > 0) {
      let tempVehicleData = state?.data?.vehicles
        .filter((vehicle_obj) => vehicle_obj?.git_status === 'success')
        .map((vehicle_obj) => {
          return {
            model_dir: vehicle_obj.vehicle_id,
            make_model_id: vehicle_obj.make_model_id,
          }
        })
      if (tempVehicleData?.length > 0) {
        var fileToSave = new Blob([toString(tempVehicleData)], {
          type: 'application/json',
        })
        downloadFileBlob(fileToSave, 'tracer-data_updates.json')
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'promoteChanges.noVehiclePromoted' }),
          },
        })
      }
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'promoteChanges.noVehiclePromoted' }),
        },
      })
    }
  }

  const downloadUpdateCSV = () => {
    modal.confirm({
      title: intl.formatMessage({ id: 'promoteChanges.downloadUpdatesTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'promoteChanges.downloadUpdatesConfirm' },
        { branchName: state?.data?.branch?.name }
      ),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'promoteChanges.download' }),
      cancelText: intl.formatMessage({ id: 'promoteChanges.cancel' }),
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({
              id: 'promoteChanges.downloadingUpdates',
            }),
            showLoader: true,
          },
          notification: {},
        })
        let res = await getRequest(
          `/api/promote_changes/download/updates/csv/`,
          true,
          true
        )
        // Was `if (res?.msg)`: a failure carrying no message fell through and
        // saved the error object as the .csv.
        if (res?.status === false || !(res instanceof Blob)) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({ id: 'promoteChanges.downloadUpdatesError' }),
            },
          })
        } else {
          downloadFileBlob(
            res,
            `summary-updates-${state?.data?.branch?.name}.csv`
          )
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'promoteChanges.fileDownloaded' }),
            },
          })
        }
      },
    })
  }

  const syncToGit = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'promoteChanges.gitSyncTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'promoteChanges.gitSyncConfirm' },
        { vehicleId: record.vehicle_id }
      ),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'promoteChanges.continue' }),
      cancelText: intl.formatMessage({ id: 'promoteChanges.cancel' }),
      onOk: async () => {
        let res = await postRequest(
          `/api/promote_changes/git/start/${record.vehicle_id}/`
        )
        if (res?.status) {
          getActiveBranchDetails(false)
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'promoteChanges.gitSyncStarted' }),
            },
          })
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({
                id: 'promoteChanges.somethingWentWrong',
              }),
            },
          })
        }
      },
    })
  }

  const onDownloadLogs = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'promoteChanges.downloadingLogs' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(
      `/api/delete/promoted-files/download/?vehicle=${record.vehicle_id}`
    )
    if (res?.status) {
      if (res?.data?.file_data?.length > 0) {
        downloadErrorLogs(
          res.data.file_data,
          `${res?.data.branch_name}-${record.vehicle_id}-delete.log`
        )
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({ id: 'promoteChanges.filesDownloaded' }),
          },
        })
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'info',
            msg: intl.formatMessage({ id: 'promoteChanges.filesIsEmpty' }),
          },
        })
      }
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({
            id: 'promoteChanges.somethingWentWrongEx',
          }),
        },
      })
    }
  }

  const loadPromotedFiles = async (vehicle) => {
    setPreviewVehicle(vehicle)
    setPreviewLoading(true)
    const res = await getRequest(
      `/api/promote_changes/promoted-files/${vehicle?.vehicle_id}/`
    )
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'promoteChanges.detailsLoadError' }))
      setPreviewLoading(false)
      return
    }
    setPreviewDetail(res)
    setPreviewLoading(false)
  }

  const onManageFiles = (record) => {
    dispatch({
      type: SHOW_SELECT_FILES_POPUP,
      data: true,
      selectedVehicle: record,
      selectedFiles: [],
    })
  }

  const onDeleteFiles = (record) => {
    setDeleteVehicle(record)
  }

  const downloadMenuItems = useMemo(
    () => [
      {
        key: 'data_updates_json',
        label: intl.formatMessage({ id: 'promoteChanges.dataUpdatesJson' }),
        icon: <FileTextOutlined />,
      },
      {
        key: 'updates_list',
        label: intl.formatMessage({ id: 'promoteChanges.dataUpdatesList' }),
        icon: <UnorderedListOutlined />,
      },
    ],
    [intl]
  )

  const statusSummary = useMemo(() => {
    const processing = filteredVehicles.filter(
      (vehicle) => vehicle?.is_processing
    ).length
    const completed = filteredVehicles.filter(
      (vehicle) => vehicle?.git_status === 'success'
    ).length
    const error = filteredVehicles.filter(
      (vehicle) => vehicle?.git_status === 'failure'
    ).length
    const cancelled = filteredVehicles.filter(
      (vehicle) => vehicle?.git_status === 'cancelled'
    ).length
    return {
      total: filteredVehicles.length,
      processing,
      completed,
      error,
      cancelled,
    }
  }, [filteredVehicles])

  const statusMenuItems = useMemo(
    () => [
      {
        key: 'all',
        icon: <UnorderedListOutlined />,
        label: (
          <FormattedMessage
            id="promoteChanges.allModels"
            values={{
              count: statusSummary.total,
              b: (chunks) => <span className="bolder">{chunks}</span>,
            }}
          />
        ),
      },
      {
        key: 'processing',
        icon: <SyncOutlined style={{ color: 'var(--color-active-primary)' }} />,
        label: (
          <FormattedMessage
            id="promoteChanges.processingModels"
            values={{
              count: statusSummary.processing,
              b: (chunks) => <span className="bolder">{chunks}</span>,
            }}
          />
        ),
      },
      {
        key: 'completed',
        icon: <CheckCircleFilled style={{ color: 'green' }} />,
        label: (
          <FormattedMessage
            id="promoteChanges.completedModels"
            values={{
              count: statusSummary.completed,
              b: (chunks) => <span className="bolder">{chunks}</span>,
            }}
          />
        ),
      },
      {
        key: 'error',
        icon: <CloseCircleFilled style={{ color: 'red' }} />,
        label: (
          <FormattedMessage
            id="promoteChanges.errorModels"
            values={{
              count: statusSummary.error,
              b: (chunks) => <span className="bolder">{chunks}</span>,
            }}
          />
        ),
      },
      {
        key: 'cancelled',
        icon: <CloseCircleFilled style={{ color: 'gray' }} />,
        label: (
          <FormattedMessage
            id="promoteChanges.cancelledModels"
            values={{
              count: statusSummary.cancelled,
              b: (chunks) => <span className="bolder">{chunks}</span>,
            }}
          />
        ),
      },
    ],
    [statusSummary]
  )

  const selectedStatusLabel = useMemo(
    () =>
      statusMenuItems.find((item) => item.key === activeStatus)?.label || null,
    [statusMenuItems, activeStatus]
  )

  const dataSource = useMemo(() => {
    const statusFilteredVehicles =
      activeStatus === 'all'
        ? filteredVehicles
        : filteredVehicles.filter((vehicle) => {
            switch (activeStatus) {
              case 'processing':
                return vehicle?.is_processing
              case 'completed':
                return vehicle?.git_status === 'success'
              case 'error':
                return vehicle?.git_status === 'failure'
              case 'cancelled':
                return vehicle?.git_status === 'cancelled'
              default:
                return true
            }
          })

    return statusFilteredVehicles.map((vehicle, index) => ({
      ...vehicle,
      key: `promote-${vehicle.vehicle_id}`,
      rowKey: `promote-${vehicle.vehicle_id}`,
      index,
    }))
  }, [filteredVehicles, activeStatus])

  const canShowPromoteMenu = state?.data?.vehicles?.length > 0
  const canLaunchPreview =
    state?.data?.remote_branch_exits &&
    (navState?.permissions?.includes(TRACER_PREVIEW) ||
      navState?.permissions?.includes(TRACER_PREVIEW_MGMT))

  const renderSubHeader = () => (
    <Header className="subheader-container">
      <Flex align="center" gap={8} className="branch-info">
        <Typography.Text strong>
          {intl.formatMessage({ id: 'promoteChanges.githubBranch' })}
        </Typography.Text>
        <Typography.Text>{state?.data?.branch?.name || '-'}</Typography.Text>
      </Flex>
      {navState?.permissions?.includes(TRACER_PREVIEW_MGMT) &&
        state?.data?.branch?.name && (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'close_branch',
                  label: intl.formatMessage({
                    id: 'promoteChanges.closeBranchBtn',
                  }),
                  icon: <DeleteOutlined />,
                  className: 'danger',
                },
              ],
              onClick: ({ key }) => {
                if (key === 'close_branch') {
                  confirmDeleteBranch()
                }
              },
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        )}
    </Header>
  )

  const renderBranchConfirm = () => (
    <Flex align="center" justify="center" style={{ padding: '48px 16px', minHeight: '60vh' }}>
      <Card variant="outlined" className="tracer-preview-centered">
        <Flex vertical gap={16} align="center">
          <ExclamationCircleOutlined className="tracer-preview-warning-icon" />
          <Typography.Text strong>
            {intl.formatMessage({ id: 'promoteChanges.branchNotExists' })}
          </Typography.Text>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'promoteChanges.branchNotFoundSubTitle' })}
          </Typography.Text>
          <Button type="primary" onClick={() => setBranchConfirmed(true)}>
            {intl.formatMessage({ id: 'promoteChanges.continue' })}
          </Button>
        </Flex>
      </Card>
    </Flex>
  )

  const renderMainContent = () => (
    <Flex vertical gap={16}>
      <Form className="full-width">
        <Flex justify="space-between" gap={16} className="full-width">
          <Flex gap={16} className="filters-wrapper full-width">
            <InputText
              name="search_value"
              placeholder={intl.formatMessage({
                id: 'promoteChanges.searchMakeModel',
              })}
              type="search"
              value={searchValue}
              isRequired={false}
              wrapperClass="search-promote-changes"
              onChange={(value) => setSearchValue(value)}
            />
          </Flex>

          <Space size="middle" align="center">
            {navState?.permissions?.includes(PROMOTE_CHANGES) && (
              <SelectVehicles />
            )}
            {canLaunchPreview && (
              <Button onClick={() => navigate(`/tracer/preview?data_version=${state?.data?.branch?.name || 'master'}`)}>
                {intl.formatMessage({
                  id: 'promoteChanges.launchTracerPreview',
                })}
              </Button>
            )}
            {canShowPromoteMenu && (
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{
                  items: downloadMenuItems,
                  onClick: ({ key }) => {
                    if (key === 'data_updates_json') {
                      downloadUpdateJson()
                    } else if (key === 'updates_list') {
                      downloadUpdateCSV()
                    }
                  },
                }}
              >
                <Button>
                  <FormattedMessage id="promoteChanges.dataUpdates" />{' '}
                  <DownOutlined />
                </Button>
              </Dropdown>
            )}

            <Dropdown
              trigger={['click']}
              placement="bottomLeft"
              menu={{
                items: statusMenuItems,
                selectable: true,
                selectedKeys: [activeStatus],
                onClick: ({ key }) => setActiveStatus(key),
              }}
            >
              <Button className="promote-changes-status-filter">
                {selectedStatusLabel} <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        </Flex>
      </Form>

      <PromoteChangesTable
        dataSource={dataSource}
        onViewPromotedFiles={loadPromotedFiles}
        onManageFiles={onManageFiles}
        onDeleteFiles={onDeleteFiles}
        onSyncToGit={syncToGit}
        onDownloadLogs={onDownloadLogs}
        canDeleteFiles={navState?.permissions?.includes(DELETE_PROMOTED_FILES)}
        canSyncToGit={navState?.permissions?.includes(PUSH_TO_GIT)}
      />
    </Flex>
  )

  return (
    <>
      {contextHolder}
      <Layout className="promote-changes-root">
        {branchConfirmed !== false && renderSubHeader()}
        <Layout className="promote-changes-content">
          <Content className="page">
            {branchConfirmed === null ? (
              <Flex align="center" justify="center" style={{ padding: '64px' }}>
                <Spin size="large" />
              </Flex>
            ) : branchConfirmed === false ? (
              renderBranchConfirm()
            ) : (
              renderMainContent()
            )}
          </Content>
        </Layout>
      </Layout>

      {state.show_select_files_popup && (
        <SelectFilesModal getActiveBranchDetails={getActiveBranchDetails} />
      )}
      {previewVehicle && (
        <PromotedFilesModal
          vehicle={previewVehicle}
          detail={previewDetail}
          loading={previewLoading}
          vehicles={state?.data?.vehicles || []}
          onSelectVehicle={loadPromotedFiles}
          onClose={() => setPreviewVehicle(null)}
        />
      )}
      {deleteVehicle && (
        <DeleteFilesModal
          vehicle={deleteVehicle}
          onClose={() => setDeleteVehicle(null)}
          onDeleted={getActiveBranchDetails}
        />
      )}
    </>
  )
}

export default PromoteChangesList
